import React from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import utils from "../../universal/utils";

const basePath = utils.getEdgeBasePath();

export default function SteamProfileCompletion({ user, initialPendingEmail }) {
  const hasSavedName = !!(user?.name?.trim());
  const emailAlreadyVerified = !!user?.emailVerifiedAt;
  const verifiedEmail = emailAlreadyVerified ? String(user?.email || "").trim() : "";

  const [emailValue, setEmailValue] = React.useState(
    initialPendingEmail || verifiedEmail || ""
  );
  const [emailSending, setEmailSending] = React.useState(false);
  const [emailError, setEmailError] = React.useState(null);
  const [pendingEmail, setPendingEmail] = React.useState(initialPendingEmail || null);
  const [resendCountdown, setResendCountdown] = React.useState(0);
  const [editingEmail, setEditingEmail] = React.useState(false);

  React.useEffect(() => {
    if (initialPendingEmail) {
      setPendingEmail(initialPendingEmail);
      if (!editingEmail && !emailAlreadyVerified) {
        setEmailValue(initialPendingEmail);
      }
    }
  }, [editingEmail, emailAlreadyVerified, initialPendingEmail]);

  React.useEffect(() => {
    if (emailAlreadyVerified && verifiedEmail) {
      setEmailValue(verifiedEmail);
      setPendingEmail(null);
      setEditingEmail(false);
      setEmailError(null);
    }
  }, [emailAlreadyVerified, verifiedEmail]);

  React.useEffect(() => {
    if (resendCountdown <= 0) return;
    const id = setTimeout(() => setResendCountdown((count) => count - 1), 1000);
    return () => clearTimeout(id);
  }, [resendCountdown]);

  function scrollToFullNameField() {
    const field = document.getElementById("account-full-name");
    if (!field) return;

    field.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });

    window.setTimeout(() => {
      try {
        field.focus({ preventScroll: true });
      } catch (_) {
        field.focus();
      }
    }, 250);
  }

  async function handleSendVerificationEmail(targetEmail = emailValue) {
    const trimmed = String(targetEmail || "").trim();
    if (!trimmed) {
      setEmailError("Please enter your email address.");
      return;
    }

    setEmailSending(true);
    setEmailError(null);
    try {
      const res = await fetch(`${basePath}/api/v1/account/request-email-verification`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmed }),
      });
      const data = await res.json();
      if (!res.ok || !data?.ok) {
        throw new Error(data?.message || "Failed to send verification email");
      }

      const normalizedEmail = data.pendingEmail || trimmed;
      setPendingEmail(normalizedEmail);
      setEmailValue(normalizedEmail);
      setEditingEmail(false);
      setResendCountdown(60);
    } catch (err) {
      setEmailError(err.message || "Something went wrong. Please try again.");
    } finally {
      setEmailSending(false);
    }
  }

  const emailStatus = emailAlreadyVerified
    ? "verified"
    : pendingEmail && !editingEmail
      ? "pending"
      : "missing";

  const displayedEmail = emailAlreadyVerified
    ? verifiedEmail
    : editingEmail
      ? emailValue
      : pendingEmail || emailValue;

  const statusChipProps =
    emailStatus === "verified"
      ? { label: "Verified", color: "success" }
      : emailStatus === "pending"
        ? { label: "Verification pending", color: "warning" }
        : { label: "Recommended", color: "default" };

  const helperText = emailError
    ? emailError
    : emailStatus === "verified"
      ? "This email address has been verified and is linked to your Steam sign-in."
      : emailStatus === "pending"
        ? "We sent a verification link to this address. Click it to confirm mailbox access."
        : "We will send a one-time verification link to this address. You can skip this, but you will need to check status in-app.";

  return (
    <Box
      sx={{
        border: "1px solid rgba(232, 163, 61,0.35)",
        borderRadius: 2,
        p: 3,
        mb: 3,
        backgroundColor: "rgba(232, 163, 61,0.04)",
      }}
    >
      <Typography
        variant="subtitle1"
        fontWeight={700}
        gutterBottom
        sx={{ color: "#E8A33D" }}
      >
        {emailAlreadyVerified ? "Steam sign-in details" : "Improve your Steam profile"}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {emailAlreadyVerified
          ? "These are the details currently linked to your Steam sign-in."
          : "You signed in with Steam, so your application can already be reviewed. Email and profile details are still recommended so we can update you and build a stronger trust profile."}
      </Typography>
      <Alert severity="warning" sx={{ mb: 2 }}>
        <AlertTitle>Steam profile visibility required</AlertTitle>
        Your Steam profile must be public with CS2 hours visible. Private
        profiles cannot be reviewed. You can update this in your Steam privacy
        settings.
      </Alert>
      {!emailAlreadyVerified && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <AlertTitle>Email is highly recommended</AlertTitle>
          Adding your email lets us contact you about application status and
          important account updates. Without an email on file, you will need to
          sign back in with Steam to check the status of your application.
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
          Recommended — Legal full name
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Use the main account form below for your legal name, then save it
          together with any other profile details you want to add.
        </Typography>
        {hasSavedName ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <CheckCircleOutlineIcon fontSize="small" sx={{ color: "success.main" }} />
            <Typography variant="body2" color="text.secondary">
              Current value: <strong>{user.name.trim()}</strong>
            </Typography>
            <Button
              size="small"
              variant="text"
              onClick={scrollToFullNameField}
              sx={{ ml: 1, fontSize: "0.75rem" }}
            >
              Edit below
            </Button>
          </Box>
        ) : (
          <Button
            variant="contained"
            size="small"
            onClick={scrollToFullNameField}
          >
            Go to full name field
          </Button>
        )}
      </Box>

      <Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            flexWrap: "wrap",
            mb: 1,
          }}
        >
          <Typography variant="body2" fontWeight={600}>
            Recommended — Email address
          </Typography>
          <Chip size="small" {...statusChipProps} />
        </Box>

        {emailStatus === "pending" ? (
          <Alert
            severity="info"
            icon={<MarkEmailReadOutlinedIcon />}
            sx={{ mb: 1.5 }}
          >
            <AlertTitle>Verification email sent</AlertTitle>
            We sent a verification link to <strong>{pendingEmail}</strong>. Click
            the link in that email to confirm your address. You can close this
            page while you wait.
          </Alert>
        ) : null}

        <TextField
          label="Email address"
          type="email"
          value={displayedEmail}
          onChange={(e) => {
            setEmailValue(e.target.value);
            setEmailError(null);
          }}
          onKeyDown={(e) =>
            e.key === "Enter" && emailStatus !== "verified"
              ? handleSendVerificationEmail()
              : undefined
          }
          error={!!emailError}
          helperText={helperText}
          size="small"
          fullWidth
          sx={{ mb: 1.5 }}
          inputProps={{
            maxLength: 254,
            readOnly: emailStatus === "verified" || (emailStatus === "pending" && !editingEmail),
          }}
        />

        {emailStatus === "verified" ? null : emailStatus === "pending" && !editingEmail ? (
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              size="small"
              onClick={() => handleSendVerificationEmail(pendingEmail)}
              disabled={emailSending || resendCountdown > 0}
            >
              {emailSending ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
              {resendCountdown > 0
                ? `Resend in ${resendCountdown}s`
                : "Resend verification email"}
            </Button>
            <Button
              variant="text"
              size="small"
              onClick={() => {
                setEditingEmail(true);
                setEmailValue(pendingEmail || "");
                setEmailError(null);
              }}
            >
              Change address
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              size="small"
              onClick={() => handleSendVerificationEmail()}
              disabled={emailSending || !String(emailValue || "").trim()}
            >
              {emailSending ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
              {pendingEmail ? "Send to updated address" : "Send verification email"}
            </Button>
            {pendingEmail ? (
              <Button
                variant="text"
                size="small"
                onClick={() => {
                  setEditingEmail(false);
                  setEmailValue(pendingEmail);
                  setEmailError(null);
                }}
              >
                Cancel
              </Button>
            ) : null}
          </Box>
        )}
      </Box>
    </Box>
  );
}

SteamProfileCompletion.propTypes = {
  user: PropTypes.shape({
    name: PropTypes.string,
    email: PropTypes.string,
    emailIsSynthetic: PropTypes.bool,
    emailVerifiedAt: PropTypes.any,
  }).isRequired,
  initialPendingEmail: PropTypes.string,
};
